import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Paper, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AccountBalance,
  SwapHoriz as VirementIcon,
  Description as ChequeIcon,
  Receipt as LcnIcon,
  LocalAtm as EspeceIcon,
} from '@mui/icons-material';
import { tresorerieAPI, TresorerieResponse, TresorerieModeTotal } from '../../services/api';

const Tresorerie: React.FC = () => {
  const [data, setData] = useState<TresorerieResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await tresorerieAPI.get();
        setData(response);
      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error('Error fetching tresorerie data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount);

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'virement': return <VirementIcon />;
      case 'cheque': return <ChequeIcon />;
      case 'lcn': return <LcnIcon />;
      case 'espece': return <EspeceIcon />;
      default: return <AccountBalance />;
    }
  };

  const getModeLabel = (mode: string) => {
    switch (mode) {
      case 'virement': return 'Virement';
      case 'cheque': return 'Chèque';
      case 'lcn': return 'LCN';
      case 'espece': return 'Espèce';
      default: return mode;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || 'Erreur de chargement'}</Typography>
      </Box>
    );
  }

  const modes = ['virement', 'cheque', 'lcn', 'espece'] as const;

  const renderTotalsTable = (
    title: string,
    totals: { general: TresorerieModeTotal; virement: TresorerieModeTotal; cheque: TresorerieModeTotal; lcn: TresorerieModeTotal; espece: TresorerieModeTotal },
    color: string,
    isEncaissement: boolean
  ) => (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isEncaissement ? <TrendingUp /> : <TrendingDown />}
        {title}
      </Typography>

      {/* General Total Card */}
      <Card sx={{ mb: 2, bgcolor: isEncaissement ? '#e8f5e9' : '#ffebee' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Total Général</Typography>
              <Typography variant="h6" fontWeight="bold" color={color}>
                {formatAmount(totals.general.total)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Performant</Typography>
              <Typography variant="body1" fontWeight="500" color="success.main">
                {formatAmount(totals.general.performant)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Encours</Typography>
              <Typography variant="body1" fontWeight="500" color="warning.main">
                {formatAmount(totals.general.encours)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Totals by Mode Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Mode Paiement</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Performant</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Encours</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modes.map((mode) => (
              <TableRow key={mode} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getModeIcon(mode)}
                    {getModeLabel(mode)}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>
                  {formatAmount(totals[mode].performant)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'warning.main' }}>
                  {formatAmount(totals[mode].encours)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatAmount(totals[mode].total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 3 }}>
        Trésorerie
      </Typography>

      {/* Solde Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: data.solde.total >= 0 ? '#e3f2fd' : '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalance sx={{ fontSize: 40, color: data.solde.total >= 0 ? '#2196f3' : '#ff9800' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Solde Total</Typography>
                <Typography variant="h5" fontWeight="bold" color={data.solde.total >= 0 ? 'info.main' : 'warning.main'}>
                  {formatAmount(data.solde.total)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUp sx={{ fontSize: 40, color: '#4caf50' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Solde Performant</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatAmount(data.solde.performant)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingDown sx={{ fontSize: 40, color: '#ff9800' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Solde Encours</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {formatAmount(data.solde.encours)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Encaissements and Décaissements Tables */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderTotalsTable('Encaissements', data.encaissements, '#4caf50', true)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTotalsTable('Décaissements', data.decaissements, '#f44336', false)}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Tresorerie;
